import { Callout, Input } from '@aecom-ai-engineering/react-components';
import { Info, Search as SearchIcon, TextSearch } from 'lucide-react';
import { type FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/empty-state';
import { AppShell } from '@/layouts/app-shell/app-shell';

import { agentSlug } from './agent-details-content';
import { AgentDetailsDialog } from './agent-details-dialog';
import { AgentsDirectoryGrid } from './agents-directory-grid';
import { AgentsDirectoryToolbar } from './agents-directory-toolbar';
import { AgentsIntroDialog } from './agents-intro-dialog';
import { useAgentsDirectory } from './use-agents-directory';
import { useAgentsIntro } from './use-agents-intro';
import { useOpenAgent } from './use-open-agent';

/** The global agents directory. Figma 11233:58407. */
export const AgentsDirectoryPage: FC = () => {
  const { t } = useTranslation();
  const {
    catalogue,
    agents,
    tags,
    category,
    onCategoryChange,
    selectedTags,
    onSelectedTagsChange,
    searchText,
    onSearch,
  } = useAgentsDirectory();
  // First visit only, and only here: the card introduces this page, and the
  // page is already behind the redesign flag via agents-directory-route.tsx.
  const intro = useAgentsIntro();
  /*
   * Where the intro card hands focus back. The heading rather than the search
   * box: it names what the card was introducing, and landing in a text input
   * would put a cursor somewhere the user did not ask for one.
   */
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Here and not in the grid: a link can open an agent the filters hide, and
  // the grid is not rendered at all when nothing matches.
  const { openAgent, openDetails, closeDetails } = useOpenAgent(catalogue);
  // The card's button when a click opened the dialog. A mouse click focuses
  // nothing, and Ark would otherwise return focus to <body>.
  const triggerRef = useRef<HTMLElement | null>(null);
  // Kept past close, which is when Ark asks where focus goes.
  const shownSlugRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (openAgent !== null) shownSlugRef.current = agentSlug(openAgent.serviceName);
  }, [openAgent]);

  // A link opened it, so no card was clicked: the agent's card if the filters
  // show it, and the heading if they do not.
  const finalFocusEl = (): HTMLElement | null => {
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (trigger?.isConnected) return trigger;

    const card = document.querySelector<HTMLElement>(
      `[data-agent-slug="${shownSlugRef.current}"] button[aria-haspopup="dialog"]`,
    );
    return card ?? headingRef.current;
  };

  // A single crumb, no Home ahead of it: the directory is a top-level
  // destination of its own in the side nav, not somewhere reached through the
  // project list. As drawn.
  //
  // `navbar.menu.agents` here and in the side-nav row — "Agents" — against the
  // heading's `agents-directory.title`, "Agents Directory". The two say
  // different things on purpose: the crumb and the row name the destination and
  // have to stay short, while the heading names the page as the design titles
  // it. `agents-directory-keys.test.ts` used to pin them equal, and no longer
  // does.
  return (
    <AppShell breadcrumbs={[{ label: t('navbar.menu.agents') }]}>
      <div className="flex items-start justify-between gap-6">
        {/* --ds-color-text-primary, deliberately, and not the project list's
            legacy text-day-neutral-dark. Aligning that page is a follow-up. */}
        {/* tabIndex -1 so the intro card can return focus here; not tab-reachable. */}
        <h1
          className="text-2xl font-medium leading-[1.2] tracking-[-0.5px] text-[var(--ds-color-text-primary)]"
          ref={headingRef}
          tabIndex={-1}
        >
          {t('agents-directory.title')}
        </h1>

        {/* w-60 is the design's 240px; Input puts className on its shell.
            type="search" keeps the browser's native clear affordance. */}
        <Input
          className="w-60 shrink-0"
          icon={SearchIcon}
          label={t('agents-directory.search')}
          onChange={onSearch}
          placeholder={t('agents-directory.search')}
          size="md"
          type="search"
          value={searchText}
        />
      </div>

      {/*
       * The directory lists every agent the product offers, not what this user
       * can run: a project decides that. Callout's own 10px radius and 13px type
       * are smaller than the design's 8px and 14px; utilities outrank its
       * components layer.
       */}
      <div className="pt-4">
        <Callout
          className="rounded-[var(--ds-radius-8)] text-[length:var(--ds-typography-paragraph-small-font-size)] [--ds-callout-bg:var(--ds-color-bg-brand-light)]"
          icon={Info}
          label={t('agents-directory.access-note-label')}
        >
          {t('agents-directory.access-note')}
        </Callout>
      </div>

      <div className="pt-4">
        <AgentsDirectoryToolbar
          category={category}
          onCategoryChange={onCategoryChange}
          onSelectedTagsChange={onSelectedTagsChange}
          selectedTags={selectedTags}
          tags={tags}
        />
      </div>

      <div className="pt-6 pb-8">
        {agents.length > 0 ? (
          <AgentsDirectoryGrid
            agents={agents}
            onOpen={(agent, trigger) => {
              triggerRef.current = trigger;
              openDetails(agent);
            }}
          />
        ) : (
          // `announce`, because this panel only ever replaces the grid in answer
          // to something the user just typed or clicked — the catalogue is
          // static, so there is no first-load empty state to announce.
          <EmptyState
            announce
            backdrop
            description={t('agents-directory.no-results-description')}
            icon={<TextSearch size={24} strokeWidth={1.75} />}
            title={t('agents-directory.no-results-title')}
          />
        )}
      </div>

      {/*
       * Last, and portaled from here rather than mounted in the route: it is
       * drawn over the directory and reads as belonging to it, and the page is
       * what knows the content behind it has rendered.
       */}
      <AgentDetailsDialog agent={openAgent} finalFocusEl={finalFocusEl} onClose={closeDetails} />

      {/* Held back while an agent is open, so a first visit through a link
          lands on that agent rather than on a card stacked over it. */}
      <AgentsIntroDialog
        finalFocusRef={headingRef}
        onDismiss={intro.dismiss}
        open={intro.isOpen && openAgent === null}
      />
    </AppShell>
  );
};
